import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { PollWidget } from "@/components/PollWidget";
import { api } from "@/lib/api";
import { Megaphone, ListChecks, Lightbulb, BarChart3 } from "lucide-react";

export default function Polls() {
  const [params, setParams] = useSearchParams();
  const [munis, setMunis] = useState([]);
  const [muniId, setMuniId] = useState(params.get("municipality") || "");
  const [today, setToday] = useState(null);
  const [all, setAll] = useState(null);
  const [suggest, setSuggest] = useState({ title: "", detail: "", category: "" });
  const [categories, setCategories] = useState([]);
  const [suggestBusy, setSuggestBusy] = useState(false);

  useEffect(() => {
    api.get("/public/municipalities").then((r) => setMunis(r.data)).catch(() => {});
    api.get("/public/polls/categories").then((r) => setCategories(r.data)).catch(() => {});
  }, []);

  const load = useCallback(() => {
    const q = muniId ? { municipality_id: muniId } : {};
    api.get("/public/polls/today", { params: q }).then((r) => setToday(r.data)).catch(() => {});
    api.get("/public/polls", { params: q }).then((r) => setAll(r.data)).catch(() => {});
  }, [muniId]);

  useEffect(() => { load(); }, [load]);

  const changeMuni = (v) => {
    const val = v === "all" ? "" : v;
    setMuniId(val);
    setToday(null); setAll(null);
    if (val) setParams({ municipality: val }); else setParams({});
  };

  const sendSuggestion = async (e) => {
    e.preventDefault();
    setSuggestBusy(true);
    try {
      await api.post("/public/polls/suggest", {
        title: suggest.title, detail: suggest.detail || null,
        category: suggest.category || null, municipality_id: muniId || null,
        ward: suggest.ward || null,
        suggested_question: suggest.question || null,
        source_url: suggest.sourceUrl || null,
        website: "",
      });
      toast.success("Thanks — your suggestion was sent for review");
      setSuggest({ title: "", detail: "", category: "", ward: "", question: "", sourceUrl: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not send suggestion");
    } finally {
      setSuggestBusy(false);
    }
  };

  return (
    <div className="wyv-container py-10 sm:py-14">
      <Badge variant="outline" className="bg-blue-50 text-blue-800 border-blue-200 mb-3">Community Polls</Badge>
      <h1 className="font-heading font-bold text-3xl sm:text-4xl text-foreground" data-testid="polls-title">
        What does your community think?
      </h1>
      <p className="text-muted-foreground mt-2 max-w-2xl leading-relaxed">
        Simple, neutral questions about the issues where you live. Voluntary community polls — not a
        scientific opinion poll, and never connected to your voting declaration.
      </p>

      <div className="mt-5 max-w-xs">
        <Label className="text-xs text-muted-foreground">Your municipality</Label>
        <Select value={muniId || "all"} onValueChange={changeMuni}>
          <SelectTrigger className="mt-1 bg-white" data-testid="polls-muni-select"><SelectValue /></SelectTrigger>
          <SelectContent className="bg-white">
            <SelectItem value="all">All of Ontario</SelectItem>
            {munis.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="mt-8 grid lg:grid-cols-2 gap-6 items-start">
        <div className="space-y-6">
          <div>
            <h2 className="font-heading font-bold text-lg text-foreground mb-3 inline-flex items-center gap-2" data-testid="todays-question-heading">
              <Megaphone className="h-5 w-5 text-accent" /> Today's Community Question
            </h2>
            {!today ? <Skeleton className="h-64" /> : today.poll ? (
              <PollWidget poll={today.poll} municipalityId={muniId || null} />
            ) : (
              <Card><CardContent className="p-6 text-sm text-muted-foreground" data-testid="no-local-poll-note">
                No local question today — answer the community-wide question instead, or choose your municipality above.
              </CardContent></Card>
            )}
          </div>
          {today?.biggest_issue_poll && (
            <div>
              <h2 className="font-heading font-bold text-lg text-foreground mb-3 inline-flex items-center gap-2" data-testid="biggest-issue-heading">
                <BarChart3 className="h-5 w-5 text-accent" /> What's the Biggest Issue in Your Community?
              </h2>
              <PollWidget poll={today.biggest_issue_poll} municipalityId={muniId || null} />
            </div>
          )}
        </div>

        <div className="space-y-6">
          <div>
            <h2 className="font-heading font-bold text-lg text-foreground mb-3 inline-flex items-center gap-2">
              <ListChecks className="h-5 w-5 text-accent" /> All Community Polls
            </h2>
            {!all ? <Skeleton className="h-40" /> : (
              <div className="space-y-3" data-testid="all-polls-list">
                {all.polls.map((p) => (
                  <PollWidget key={p.id} poll={p} municipalityId={muniId || null} compact />
                ))}
                {all.polls.length === 0 && <p className="text-sm text-muted-foreground">No polls published yet.</p>}
              </div>
            )}
          </div>

          <Card data-testid="suggest-issue-card">
            <CardContent className="p-6">
              <h2 className="font-heading font-bold text-base text-foreground inline-flex items-center gap-2">
                <Lightbulb className="h-4 w-4 text-accent" /> What should we ask next?
              </h2>
              <p className="text-xs text-muted-foreground mt-1">
                Think we should ask about something in your community? Suggestions are reviewed by an
                administrator before any poll is created — nothing publishes automatically.
              </p>
              <form onSubmit={sendSuggestion} className="mt-4 space-y-3">
                <Input placeholder="What issue should we ask about?" value={suggest.title} maxLength={300}
                       onChange={(e) => setSuggest({ ...suggest, title: e.target.value })} data-testid="suggest-title-input" />
                <Input placeholder="Suggested question wording (optional)" value={suggest.question || ""} maxLength={400}
                       onChange={(e) => setSuggest({ ...suggest, question: e.target.value })} data-testid="suggest-question-input" />
                <Textarea placeholder="Short explanation — why does this matter? (optional)" rows={2} maxLength={1000} value={suggest.detail}
                          onChange={(e) => setSuggest({ ...suggest, detail: e.target.value })} data-testid="suggest-detail-input" />
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <Input placeholder="Ward (optional)" value={suggest.ward || ""} maxLength={120}
                         onChange={(e) => setSuggest({ ...suggest, ward: e.target.value })} data-testid="suggest-ward-input" />
                  <Input placeholder="Source link (optional)" value={suggest.sourceUrl || ""} maxLength={800}
                         onChange={(e) => setSuggest({ ...suggest, sourceUrl: e.target.value })} data-testid="suggest-source-input" />
                </div>
                <Select value={suggest.category || undefined} onValueChange={(v) => setSuggest({ ...suggest, category: v })}>
                  <SelectTrigger className="bg-white" data-testid="suggest-category-select"><SelectValue placeholder="Category (optional)" /></SelectTrigger>
                  <SelectContent className="bg-white">
                    {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Button type="submit" size="sm" disabled={suggestBusy || suggest.title.trim().length < 5} data-testid="suggest-submit">
                  {suggestBusy ? "Sending…" : "Send Suggestion"}
                </Button>
              </form>
            </CardContent>
          </Card>

          <Button asChild variant="outline" className="w-full" data-testid="community-voice-link">
            <Link to={`/community-voice${muniId ? `?municipality=${muniId}` : ""}`}>See Community Results →</Link>
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-muted-foreground leading-relaxed mt-10 max-w-3xl" data-testid="polls-page-disclaimer">
        Community Poll results reflect voluntary participation on WitnessYourVote. They are not a scientific
        opinion poll and should not be treated as representative of every voter in the municipality. Poll answers
        are never connected to your WitnessYourVote declaration, receipt or verification record.
      </p>
    </div>
  );
}
